import React from 'react';

import { useForm, FormProvider } from 'react-hook-form';

import { useSelector } from 'react-redux';

import { zodResolver } from '@hookform/resolvers/zod';
import { dobZodField } from '@vvoyages/shared-ui/ui';
import { z } from 'zod';

import { DateOfBirthField } from '@/components/DateOfBirthField';
import { appUiIds } from '@/constants/appUiIds';
import { DOB_VALIDATION, getDobValidationMessages } from '@/constants/dobValidation';
import useAppUiData from '@/hooks/useAppUiData';
import { selectResources } from '@/reducers/common/selectors';

import ClaimBookingSuccessLayout from './ClaimBookingSuccessLayout';
import { StepData } from '../types/ClaimBookingTypes';

type ClaimBookingDobUpdateOTPProps = {
  tabId: string;
  data: StepData;
  // eslint-disable-next-line no-unused-vars
  onSubmit: (dob: string) => void;
  onCancel: () => void;
};

const ClaimBookingDobUpdateOTP = ({ tabId, data, onSubmit, onCancel }: ClaimBookingDobUpdateOTPProps) => {
  const appUiData = useAppUiData();
  const resources = useSelector(selectResources);
  const dobValidation = getDobValidationMessages(resources, 'account');
  const sailingStartDate = data?.reservationDetails?.fromDate;

  const STEP_CONFIG = {
    title: appUiData[appUiIds.claimBooking.otpSuccessTitle],
    subtitle: appUiData[appUiIds.claimBooking.emailOrDobSelectOtpSubtitle],
    description: appUiData[appUiIds.claimBooking.dobUpdateOtpDescription],
  } as const;

  const schema = z.object({
    dob: dobZodField({
      ...dobValidation,
      minAge: DOB_VALIDATION.minAge.account,
      maxAge: DOB_VALIDATION.maxAge,
      serverISOTime: sailingStartDate,
    }),
  });

  const methods = useForm({
    resolver: zodResolver(schema),
    mode: 'all',
    defaultValues: { dob: '' },
  });

  const {
    control,
    handleSubmit,
    watch,
    formState: { isValid },
  } = methods;

  const values = watch();
  const isFormFilled = Object.values(values).every(Boolean) && isValid;

  const onFormSubmit = handleSubmit(({ dob }) => {
    onSubmit(dob);
  });

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <FormProvider {...methods}>
      <ClaimBookingSuccessLayout
        tabId={tabId}
        messages={STEP_CONFIG}
        onSubmit={onFormSubmit}
        onCancel={onCancel}
        isSubmitDisabled={!isFormFilled}
      >
        <DateOfBirthField
          name="dob"
          control={control}
          label="Date of Birth"
          required
          requiredMessage={dobValidation.requiredMessage}
          invalidYearMessage={dobValidation.invalidYearMessage}
          minAge={DOB_VALIDATION.minAge.account}
          maxAge={DOB_VALIDATION.maxAge}
          serverISOTime={sailingStartDate}
          labelVariant="fieldGroup"
        />
      </ClaimBookingSuccessLayout>
    </FormProvider>
  );
};

export default ClaimBookingDobUpdateOTP;
